/**
 * OCR Service for Smart Records Capture
 *
 * Uses Tesseract.js for client-side OCR processing of scanned documents.
 * Extracts text and attempts to identify key record metadata.
 */

import Tesseract from 'tesseract.js';

export interface OCRResult {
  text: string;
  confidence: number;
  extractedData: ExtractedRecordData;
  processingTime: number;
}

export interface ExtractedRecordData {
  staffName?: string;
  staffId?: string;
  dateOfBirth?: string;
  appointmentDate?: string;
  grade?: string;
  salary?: string;
  department?: string;
  documentType?: RecordDocumentType;
  dates: string[];
  fileNumbers: string[];
  signatures: number;
}

export type RecordDocumentType =
  | 'appointment_letter'
  | 'promotion_letter'
  | 'transfer_letter'
  | 'leave_record'
  | 'disciplinary_action'
  | 'training_certificate'
  | 'performance_evaluation'
  | 'personal_document'
  | 'unknown';

// Ghana-specific patterns
const PATTERNS = {
  // Staff ID patterns (e.g., GCS-12345, EMP001, PS/2024/001)
  staffId: /\b(?:GCS|EMP|PS|GES|GHS|GPS)[\/\-]?\d{2,6}[\/\-]?\d{0,4}\b/gi,

  // File numbers (e.g., PF/OHCS/2024/001, CONF/MOH/23/045)
  fileNumber: /\b(?:PF|CONF|ADM|HR)[\/\-][A-Z]{2,10}[\/\-]\d{2,4}[\/\-]\d{1,5}\b/gi,

  // Dates in various formats
  date: /\b\d{1,2}[\/\-\.]\d{1,2}[\/\-\.]\d{2,4}\b|\b\d{1,2}(?:st|nd|rd|th)?\s+(?:January|February|March|April|May|June|July|August|September|October|November|December)\s*,?\s*\d{4}\b/gi,

  // Grade levels (e.g., Grade 12, GS-14, Level 5)
  grade: /\b(?:Grade|GS|Level|Rank)\s*[:-]?\s*\d{1,2}[A-Z]?\b/gi,

  // Salary (e.g., GHS 5,000.00, GH₵ 12,345)
  salary: /\b(?:GHS|GH₵|GHC)\s*[\d,]+(?:\.\d{2})?\b/gi,

  // Ghana names (common patterns)
  ghanaName: /\b(?:Mr\.|Mrs\.|Ms\.|Dr\.|Prof\.)\s+[A-Z][a-z]+\s+[A-Z][a-z]+(?:\s+[A-Z][a-z]+)?\b/g,
};

// Document type keywords
const DOCUMENT_KEYWORDS: Record<RecordDocumentType, string[]> = {
  appointment_letter: ['appointment', 'appointed', 'hereby appoint', 'offer of appointment', 'employment offer'],
  promotion_letter: ['promotion', 'promoted', 'hereby promote', 'advancement', 'upgraded'],
  transfer_letter: ['transfer', 'transferred', 'posting', 'reassignment', 'secondment'],
  leave_record: ['leave', 'annual leave', 'sick leave', 'maternity', 'vacation', 'absence'],
  disciplinary_action: ['disciplinary', 'misconduct', 'warning', 'suspension', 'query', 'sanction'],
  training_certificate: ['certificate', 'training', 'course', 'workshop', 'seminar', 'completed'],
  performance_evaluation: ['performance', 'appraisal', 'evaluation', 'assessment', 'review'],
  personal_document: ['birth certificate', 'passport', 'national id', 'ghana card', 'certificate'],
  unknown: [],
};

class OCRService {
  private worker: Tesseract.Worker | null = null;
  private isInitialized = false;

  /**
   * Initialize the Tesseract worker
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    console.log('[OCR] Initializing Tesseract worker...');

    this.worker = await Tesseract.createWorker('eng', 1, {
      logger: (m) => {
        if (m.status === 'recognizing text') {
          console.log(`[OCR] Progress: ${Math.round(m.progress * 100)}%`);
        }
      },
    });

    this.isInitialized = true;
    console.log('[OCR] Tesseract worker initialized');
  }

  /**
   * Process an image and extract text with metadata
   */
  async processImage(imageSource: File | Blob | string): Promise<OCRResult> {
    const startTime = performance.now();

    if (!this.isInitialized) {
      await this.initialize();
    }

    if (!this.worker) {
      throw new Error('OCR worker not initialized');
    }

    console.log('[OCR] Processing image...');

    const result = await this.worker.recognize(imageSource);
    const text = result.data.text;
    const confidence = result.data.confidence;

    const extractedData = this.extractRecordData(text);
    const processingTime = performance.now() - startTime;

    console.log(`[OCR] Completed in ${processingTime.toFixed(0)}ms with ${confidence.toFixed(1)}% confidence`);

    return {
      text,
      confidence,
      extractedData,
      processingTime,
    };
  }

  /**
   * Process multiple images (e.g., multi-page document)
   */
  async processMultipleImages(images: (File | Blob)[]): Promise<OCRResult> {
    const results: OCRResult[] = [];

    for (const image of images) {
      const result = await this.processImage(image);
      results.push(result);
    }

    // Combine results
    const combinedText = results.map(r => r.text).join('\n\n--- Page Break ---\n\n');
    const avgConfidence = results.reduce((sum, r) => sum + r.confidence, 0) / results.length;
    const totalTime = results.reduce((sum, r) => sum + r.processingTime, 0);

    // Re-extract from combined text for better accuracy
    const extractedData = this.extractRecordData(combinedText);

    return {
      text: combinedText,
      confidence: avgConfidence,
      extractedData,
      processingTime: totalTime,
    };
  }

  /**
   * Extract structured record data from OCR text
   */
  private extractRecordData(text: string): ExtractedRecordData {
    const normalizedText = text.toLowerCase();

    // Extract patterns
    const staffIds = text.match(PATTERNS.staffId) || [];
    const fileNumbers = text.match(PATTERNS.fileNumber) || [];
    const dates = text.match(PATTERNS.date) || [];
    const grades = text.match(PATTERNS.grade) || [];
    const salaries = text.match(PATTERNS.salary) || [];
    const names = text.match(PATTERNS.ghanaName) || [];

    // Detect document type
    const documentType = this.detectDocumentType(normalizedText);

    // Count potential signatures (lines that look like signatures)
    const signaturePatterns = text.match(/_{10,}|\.{10,}|\-{10,}/g) || [];
    const signatures = signaturePatterns.length;

    return {
      staffName: names[0]?.replace(/^(Mr\.|Mrs\.|Ms\.|Dr\.|Prof\.)\s+/, ''),
      staffId: staffIds[0],
      grade: grades[0],
      salary: salaries[0],
      documentType,
      dates: [...new Set(dates)],
      fileNumbers: [...new Set(fileNumbers)],
      signatures,
    };
  }

  /**
   * Detect the type of record document
   */
  private detectDocumentType(text: string): RecordDocumentType {
    let bestMatch: RecordDocumentType = 'unknown';
    let maxScore = 0;

    for (const [docType, keywords] of Object.entries(DOCUMENT_KEYWORDS)) {
      if (docType === 'unknown') continue;

      const score = keywords.reduce((count, keyword) => {
        return count + (text.includes(keyword.toLowerCase()) ? 1 : 0);
      }, 0);

      if (score > maxScore) {
        maxScore = score;
        bestMatch = docType as RecordDocumentType;
      }
    }

    return bestMatch;
  }

  /**
   * Enhance image before OCR (placeholder for future implementation)
   */
  async enhanceImage(image: File | Blob): Promise<Blob> {
    // Future: Apply image processing (contrast, deskew, denoise)
    // For now, return original
    return image instanceof File ? image : image;
  }

  /**
   * Cleanup resources
   */
  async terminate(): Promise<void> {
    if (this.worker) {
      await this.worker.terminate();
      this.worker = null;
      this.isInitialized = false;
      console.log('[OCR] Tesseract worker terminated');
    }
  }
}

// Export singleton instance
export const ocrService = new OCRService();

// Export types
export type { OCRService };
